/**
 * Wrapper for Telegram CloudStorage API with promisification and error handling
 */

import { RETRY_CONFIG } from './constants';

export type CloudStorageError = Error & {
  code?: string;
};

/**
 * CloudStorage API interface
 */
export interface CloudStorageApi {
  isAvailable(): boolean;
  getItem(key: string): Promise<string | undefined>;
  setItem(key: string, value: string): Promise<void>;
  getItems(keys: string[]): Promise<Record<string, string>>;
  removeItem(key: string): Promise<void>;
  removeItems(keys: string[]): Promise<void>;
  getKeys(): Promise<string[]>;
}

/**
 * Get Telegram CloudStorage API if available
 */
function getTelegramCloudStorage() {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window.Telegram?.WebApp?.CloudStorage;
}

/**
 * Check if CloudStorage is available
 */
export function isCloudStorageAvailable(): boolean {
  const cloudStorage = getTelegramCloudStorage();
  return Boolean(
    cloudStorage
    && typeof cloudStorage.setItem === 'function'
    && typeof cloudStorage.getItem === 'function',
  );
}

/**
 * Sleep utility for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Calculate exponential backoff delay
 */
function getRetryDelay(attempt: number): number {
  const delay = RETRY_CONFIG.baseDelay * Math.pow(2, attempt);
  return Math.min(delay, RETRY_CONFIG.maxDelay);
}

/**
 * Execute operation with retry logic
 */
async function withRetry<T>(
  operation: () => Promise<T>,
  operationName: string,
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt < RETRY_CONFIG.maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;

      // Don't retry on quota exceeded
      if ((error as CloudStorageError).code === 'QUOTA_EXCEEDED') {
        throw error;
      }

      // Don't retry on last attempt
      if (attempt === RETRY_CONFIG.maxAttempts - 1) {
        break;
      }

      // Wait before retry
      const delay = getRetryDelay(attempt);
      // eslint-disable-next-line no-console
      console.warn(
        `[CloudStorage] ${operationName} failed (attempt ${attempt + 1}/${RETRY_CONFIG.maxAttempts}), `
        + `retrying in ${delay}ms`,
        error,
      );
      await sleep(delay);
    }
  }

  throw new Error(`[CloudStorage] ${operationName} failed after ${RETRY_CONFIG.maxAttempts} attempts: ${lastError}`);
}

/**
 * Promisify CloudStorage.setItem
 */
function setItemPromise(key: string, value: string): Promise<void> {
  const cloudStorage = getTelegramCloudStorage();
  if (!cloudStorage) {
    return Promise.reject(new Error('CloudStorage not available'));
  }

  return new Promise((resolve, reject) => {
    cloudStorage.setItem(key, value, (error, success) => {
      if (error) {
        const err = new Error(`Failed to set item: ${error.message || error}`) as CloudStorageError;
        err.code = (error as any).code;
        reject(err);
      } else if (!success) {
        reject(new Error('Failed to set item: operation returned false'));
      } else {
        resolve();
      }
    });
  });
}

/**
 * Promisify CloudStorage.getItem
 */
function getItemPromise(key: string): Promise<string | undefined> {
  const cloudStorage = getTelegramCloudStorage();
  if (!cloudStorage) {
    return Promise.reject(new Error('CloudStorage not available'));
  }

  return new Promise((resolve, reject) => {
    cloudStorage.getItem(key, (error, value) => {
      if (error) {
        reject(new Error(`Failed to get item: ${error.message || error}`));
      } else {
        resolve(value || undefined);
      }
    });
  });
}

/**
 * Promisify CloudStorage.getItems
 */
function getItemsPromise(keys: string[]): Promise<Record<string, string>> {
  const cloudStorage = getTelegramCloudStorage();
  if (!cloudStorage) {
    return Promise.reject(new Error('CloudStorage not available'));
  }

  return new Promise((resolve, reject) => {
    cloudStorage.getItems(keys, (error, values) => {
      if (error) {
        reject(new Error(`Failed to get items: ${error.message || error}`));
      } else {
        resolve(values || {});
      }
    });
  });
}

/**
 * Promisify CloudStorage.removeItem
 */
function removeItemPromise(key: string): Promise<void> {
  const cloudStorage = getTelegramCloudStorage();
  if (!cloudStorage) {
    return Promise.reject(new Error('CloudStorage not available'));
  }

  return new Promise((resolve, reject) => {
    cloudStorage.removeItem(key, (error, success) => {
      if (error) {
        reject(new Error(`Failed to remove item: ${error.message || error}`));
      } else if (!success) {
        reject(new Error('Failed to remove item: operation returned false'));
      } else {
        resolve();
      }
    });
  });
}

/**
 * Promisify CloudStorage.removeItems
 */
function removeItemsPromise(keys: string[]): Promise<void> {
  const cloudStorage = getTelegramCloudStorage();
  if (!cloudStorage) {
    return Promise.reject(new Error('CloudStorage not available'));
  }

  return new Promise((resolve, reject) => {
    cloudStorage.removeItems(keys, (error, success) => {
      if (error) {
        reject(new Error(`Failed to remove items: ${error.message || error}`));
      } else if (!success) {
        reject(new Error('Failed to remove items: operation returned false'));
      } else {
        resolve();
      }
    });
  });
}

/**
 * Promisify CloudStorage.getKeys
 */
function getKeysPromise(): Promise<string[]> {
  const cloudStorage = getTelegramCloudStorage();
  if (!cloudStorage) {
    return Promise.reject(new Error('CloudStorage not available'));
  }

  return new Promise((resolve, reject) => {
    cloudStorage.getKeys((error, keys) => {
      if (error) {
        reject(new Error(`Failed to get keys: ${error.message || error}`));
      } else {
        resolve(keys || []);
      }
    });
  });
}

/**
 * Create CloudStorage API instance
 */
export function createCloudStorageApi(): CloudStorageApi {
  return {
    isAvailable: isCloudStorageAvailable,

    getItem: (key: string) => withRetry(
      () => getItemPromise(key),
      `getItem(${key})`,
    ),

    setItem: (key: string, value: string) => withRetry(
      () => setItemPromise(key, value),
      `setItem(${key})`,
    ),

    getItems: (keys: string[]) => withRetry(
      () => getItemsPromise(keys),
      `getItems(${keys.length} keys)`,
    ),

    removeItem: (key: string) => withRetry(
      () => removeItemPromise(key),
      `removeItem(${key})`,
    ),

    removeItems: (keys: string[]) => withRetry(
      () => removeItemsPromise(keys),
      `removeItems(${keys.length} keys)`,
    ),

    getKeys: () => withRetry(
      () => getKeysPromise(),
      'getKeys()',
    ),
  };
}
